import { Link, LinkProps } from '@amzn/awsui-components-console';
import React from 'react';
import { useGroupName } from 'src/data/redux';
import { urlSearchString, useInternalOnFollow } from 'src/nav/navHelper';

/**
 * An extension of the Cloudscape Link component for *internal* navigation (within the Hammerstone React website).
 * Uses a custom onFollow handler for client-side routing without full page refreshes.
 *
 * @param props All standard Cloudscape LinkProps (except onFollow), plus:
 *
 * @param props.groupName Controls how the Hammerstone group is propagated during navigation:
 *   - `true` — Persists the currently selected groupName from Redux state as a URL search parameter.
 *   - `string` — Overrides the groupName search parameter with the provided value, useful when
 *     linking to a resource that belongs to a different group than the one currently selected.
 *   - `undefined | null | false` (default) — No groupName search parameter is appended.
 *
 * @param props.pathParams A map of path parameter placeholders to their values. Each key is matched
 *   as `:key` in the href and replaced with the corresponding value.
 */
export default function InternalLink(
	props: Omit<LinkProps, 'onFollow'> & { groupName?: boolean | string; pathParams?: Record<string, string | number> }
) {
	const { groupName: groupNameProp, pathParams, ...linkProps } = props;
	const onFollow = useInternalOnFollow();
	const currentGroupName = useGroupName();

	let href = Object.entries(pathParams ?? {})
		.filter(([, val]) => val != null)
		.reduce((acc, [key, val]) => {
			// Only match :key when followed by a non-word character or end of string,
			// preventing partial replacements (e.g., :activity matching inside :activityId)
			return acc.replace(new RegExp(`:${key}\\b`, 'g'), val.toString());
		}, props.href ?? '');

	if (groupNameProp === true) {
		// When groupName is simply set to true, persist the currently selected groupName
		href += urlSearchString({ groupName: currentGroupName });
	} else if (typeof groupNameProp === 'string') {
		// When a groupName string is provided, override
		href += urlSearchString({ groupName: groupNameProp });
	}

	return <Link {...linkProps} href={href} onFollow={onFollow} />;
}
